import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/context/AuthContext';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { useLocalFiltersRegistry } from '@/context/LocalFiltersRegistry/LocalFiltersRegistry';
import { useToast } from '@/storybook/components/Toast/Toast';
import { readCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { copyShareViewLink, publishSharedView } from '@/utils/filter/shareView';

interface UseShareViewResult {
  handleShareView: () => void;
  isLoading: boolean;
}

const useShareView = (): UseShareViewResult => {
  const { t } = useTranslation();
  const { filterState, optionPathsByFilter } = useContext(FiltersContext);
  const { userInfo } = useAuthContext();
  const { getAllLocalFilters } = useLocalFiltersRegistry();
  const { makeToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleShareView = useCallback(async () => {
    setIsLoading(true);
    try {
      const sharedFilterId = await publishSharedView({
        globalFilters: filterState,
        localFilters: getAllLocalFilters(),
        resolvedCountryCodes: readCachedCountryCodes(),
        optionPaths: optionPathsByFilter,
        senderCountryCodes: userInfo?.countries ?? [],
      });

      await copyShareViewLink(sharedFilterId);
      makeToast({
        content: String(t('global.filters.shareViewSuccess')),
        appearance: 'success',
        autoClose: 5000,
      });
    } catch {
      makeToast({
        content: String(t('global.filters.shareViewError')),
        appearance: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [filterState, optionPathsByFilter, userInfo, getAllLocalFilters, makeToast, t]);

  return { handleShareView, isLoading };
};

export default useShareView;
